import { AxiosResponse } from 'axios';
import { RankingFieldsModel } from '../../type/entities/ranking-type';
import { RestServiceConnection } from '../restServiceConnection';

const ENDPOINT = '/ranking';

export class RankingService extends RestServiceConnection {

    /**
     * EN: List all rankings with basic information
     *
     * ES: Listar todos los rankings con información básica
     *
     * @param filters
     * @returns
     */
    listAllRankings = async (filters?: any) => {
        this.response = await this.makeRequest({
            method: 'POST',
            url: ENDPOINT + '/all',
            data: filters || {},
            headers: { "Content-Type": "application/json" }
        }, true);
        return this;
    }

    /**
     * EN: List rankings with pagination and filters
     *
     * ES: Listar rankings con paginación y filtros
     *
     * @param filters
     * @returns
     */
    listRankings = async (filters?: any) => {
        this.response = await this.makeRequest({
            method: 'POST',
            url: ENDPOINT + '/list',
            data: filters,
        }, true) as AxiosResponse;
        return this;
    }

    /**
     * Obtiene un ranking específico por su ID
     * @param id - ID del ranking a obtener
     * @returns Promise con la respuesta del servidor
     */
    getRankingById = async (id: string) => {
        this.response = await this.makeRequest({
            method: 'POST',
            url: ENDPOINT + '/get',
            data: { id },
            headers: { "Content-Type": "application/json" }
        }, true);
        return this;
    }

    /**
     * Crea un nuevo ranking
     * @param entity - Datos del ranking a crear
     * @returns Promise con la respuesta del servidor
     */
    createRanking = async (entity: RankingFieldsModel) => {
        this.response = await this.makeRequest({
            method: 'POST',
            url: ENDPOINT + '/create',
            data: entity,
            headers: { "Content-Type": "application/json" }
        }, true);
        return this;
    }

    /**
     * Edita un ranking existente
     * @param entity - Datos del ranking a editar
     * @returns Promise con la respuesta del servidor
     */
    editRanking = async (entity: RankingFieldsModel) => {
        this.response = await this.makeRequest({
            method: 'POST',
            url: ENDPOINT + '/edit',
            data: entity,
            headers: { "Content-Type": "application/json" }
        }, true);
        return this;
    }

    /**
     * Alterna el estado activo/inactivo de un ranking
     * @param id - ID del ranking
     * @returns Promise con la respuesta del servidor
     */
    toggleRankingState = async (id: string) => {
        this.response = await this.makeRequest({
            method: 'POST',
            url: ENDPOINT + '/toggle',
            data: { id },
            headers: { "Content-Type": "application/json" }
        }, true);
        return this;
    }

    /**
     * Elimina un ranking específico
     * @param id - ID del ranking a eliminar
     * @returns Promise con la respuesta del servidor
     */
    deleteRanking = async (id: string) => {
        this.response = await this.makeRequest({
            method: 'POST',
            url: ENDPOINT + '/delete',
            data: { id },
            headers: { "Content-Type": "application/json" }
        }, true);
        return this;
    }

    /**
     * Elimina múltiples rankings
     * @param ids - Array de IDs de rankings a eliminar
     * @returns Promise con la respuesta del servidor
     */
    deleteMultipleRankings = async (ids: string[]) => {
        this.response = await this.makeRequest({
            method: 'POST',
            url: ENDPOINT + '/delete-multi',
            data: { ids },
            headers: { "Content-Type": "application/json" }
        }, true);
        return this;
    }

    // -----------------------------------------------------------------------------------------------------------------
    // Métodos para la gestión de usuarios en rankings
    // -----------------------------------------------------------------------------------------------------------------

    /**
     * Lista los usuarios de un ranking específico
     * @param filters - Filtros que incluyen el ID del ranking
     * @returns Promise con la respuesta del servidor
     */
    listRankingUsers = async (filters?: any) => {
        this.response = await this.makeRequest({
            method: 'POST',
            url: ENDPOINT + '/list-users',
            data: filters,
        }, true) as AxiosResponse;
        return this;
    }

    /**
     * Lista los usuarios no vinculados a un ranking específico
     * @param filters - Filtros que incluyen el ID del ranking
     * @returns Promise con la respuesta del servidor
     */
    listUnlinkedUsers = async (filters?: any) => {
        this.response = await this.makeRequest({
            method: 'POST',
            url: ENDPOINT + '/list-unassigned-users',
            data: filters,
        }, true) as AxiosResponse;
        return this;
    }

    /**
     * Añade usuarios a un ranking
     * @param data - Datos con el ID del ranking y los IDs de usuarios
     * @returns Promise con la respuesta del servidor
     */
    addUsersToRanking = async (data: any) => {
        this.response = await this.makeRequest({
            method: 'POST',
            url: ENDPOINT + '/add-users',
            data: data,
            headers: { "Content-Type": "application/json" }
        }, true);
        return this;
    }

    /**
     * Elimina usuarios de un ranking
     * @param data - Datos con el ID del ranking y los IDs de usuarios
     * @returns Promise con la respuesta del servidor
     */
    removeUsersFromRanking = async (data: any) => {
        this.response = await this.makeRequest({
            method: 'POST',
            url: ENDPOINT + '/delete-users',
            data: data,
            headers: { "Content-Type": "application/json" }
        }, true);
        return this;
    }

}